import {
  DerivationInput,
  DerivedFrom,
  ListRecord,
  TransactionFieldMetadata,
} from '../models/transaction-metadata.model'
import { readColumn } from './list-options'

/**
 * Field derivation — the metadata layer's Derived From column, read off what RPS SERVES
 * (PVAL-26685).
 *
 * A derivation says "this field's value comes from an upstream, given these inputs". It is authored
 * on the field that RECEIVES the value, never on the one that triggers it, which is what keeps one
 * field's metadata its whole story: Trade Currency and Settle Currency each name `instrumentId` as
 * their input, and the dialog sends one `/resolve` call carrying an item for each.
 *
 * The served document carries no URL, no reply path and no token name — `derivationInputs` is the
 * binding with all three stripped, because the browser must never receive an upstream URL. So the
 * dialog no longer builds a request at all: it reads each input's value and posts it to
 * `/resolve` as a `type: "Derivation"` item keyed by the field, and RPS answers the scalar.
 *
 * Everything here is pure. Nothing fetches — the call sits behind {@link TransactionDerivationSource},
 * so these rules are testable without a backend.
 */

/** One field's derivation, ready to act on. */
export interface FieldDerivation {
  /** The field being filled. Also the `/resolve` item's `keyName`. */
  field: string
  from: DerivedFrom
  inputs: DerivationInput[]
}

/** One `/resolve` item a derivation produces, before the transport adds `source` and `type`. */
export interface FieldDerivationRequest {
  /** The field the answer is written to. */
  field: string
  /** The row RPS resolves — the derived field's own key. */
  keyName: string
  /** Each input's value, keyed by `input.field ?? input.key`. */
  params: Record<string, string>
}

/** The name an input is read — and sent — under. */
export const inputName = (input: DerivationInput): string =>
  input.field ?? input.key ?? ''

/**
 * Blank for the purposes of a derivation.
 *
 * `0` counts as blank deliberately, following the design: every input here names an identifier —
 * an account, an instrument — and zero is the unset sentinel those APIs use, not a real id.
 */
export const isUnset = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  String(value).trim() === '' ||
  String(value).trim() === '0'

/**
 * Every derivation in a set of fields, in field order.
 *
 * A field is derived only when it names `derivedFrom` AND carries at least one input — a field
 * with no inputs has nothing to send, so it reads as "not derived" rather than as an error.
 */
export function derivationsOf(
  fields: readonly TransactionFieldMetadata[],
): FieldDerivation[] {
  return fields.flatMap(meta => {
    const inputs = (meta.derivationInputs ?? []).filter(
      input => inputName(input) !== '',
    )
    if (!meta.derivedFrom || !inputs.length) return []
    return [{ field: meta.field, from: meta.derivedFrom, inputs }]
  })
}

/**
 * Which fields to re-derive when a given field changes — the dependency graph, inverted.
 *
 * Read off the inputs rather than authored twice: `{from: 'field', field: 'instrumentId'}` on Trade
 * Currency already says "Trade Currency depends on Instrument ID". Context inputs are skipped —
 * nothing the user types triggers them.
 *
 * Returned as trigger field -> the fields naming it.
 */
export function derivationTriggers(
  derivations: readonly FieldDerivation[],
): Map<string, string[]> {
  const triggers = new Map<string, string[]>()

  for (const { field, inputs } of derivations) {
    for (const input of inputs) {
      if (input.from !== 'field' || !input.field) continue
      // A field naming itself would re-trigger forever; RPS's V13 refuses to store one, and
      // dropping it here is cheaper than discovering the loop at runtime.
      if (input.field === field) continue

      const dependents = triggers.get(input.field) ?? []
      if (!dependents.includes(field)) dependents.push(field)
      triggers.set(input.field, dependents)
    }
  }

  return triggers
}

/**
 * The `/resolve` item one derivation needs right now, or `null` when it cannot be asked yet.
 *
 * `null` the moment any input is unset — blank, `0` or absent — rather than asking RPS about a hole:
 * on a `field` derivation that is ordinary (the trigger is not filled in yet), on a `context` one it
 * means the session did not carry what the metadata asked for.
 *
 * `valueOf` reads a sibling field's current value (on a collection line, that line's cell);
 * `contextOf` reads the session the dialog was opened in.
 */
export function derivationRequest(
  derivation: FieldDerivation,
  valueOf: (field: string) => unknown,
  contextOf: (key: string) => unknown,
): FieldDerivationRequest | null {
  const params: Record<string, string> = {}
  for (const input of derivation.inputs) {
    const name = inputName(input)
    const value =
      input.from === 'context' ? contextOf(input.key ?? name) : valueOf(name)
    if (isUnset(value)) return null
    params[name] = String(value).trim()
  }
  return { field: derivation.field, keyName: derivation.field, params }
}

/** Whether a derivation reads a sibling on its own collection line. */
export const isRowScoped = (derivation: FieldDerivation): boolean =>
  derivation.inputs.some(input => input.scope === 'row')

/**
 * The value a derivation's reply stands for.
 *
 * RPS is meant to answer the scalar itself (PVAL-26675). Until it does, `/resolve` hands back the
 * raw upstream reply, and the field's served `derivationDisplayText` names the property that holds
 * the value — `externalAccountIdentifier` in a business-ws reference. An array is read through its
 * first row, which is also the only row of a `one` derivation. The name is followed through JSON
 * text too, so `externalAccountParameters.glId` reads into the string business-ws sends.
 *
 * A reply that is already a scalar passes through, as does any reply for a field that names no
 * property, so nothing changes the day RPS starts projecting. An empty reply, or a row without the
 * named property, is `null`: the upstream had nothing to say, and the dialog leaves the field alone.
 */
export function derivedScalar(
  reply: unknown,
  displayText: string | undefined,
): unknown {
  if (reply == null || typeof reply !== 'object' || !displayText) return reply
  const row = Array.isArray(reply) ? reply[0] : reply
  if (row == null || typeof row !== 'object') return row ?? null
  return readColumn(row as ListRecord, displayText, true) ?? null
}
